import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getWatchCardModalTranslations } from '../../translations/watchCardModal';
import { downloadWatchCard } from '../../api/contentApi';
import './WatchCardModal.css';

interface WatchCardModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function WatchCardModal({ isOpen, onClose }: WatchCardModalProps) {
    const { language } = useLanguage();
    const t = getWatchCardModalTranslations(language);
    
    const [serial, setSerial] = useState('');
    const [pin, setPin] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSerial('');
            setPin('');
            setMessage(null);
            setIsDownloading(false);
        }
    }, [isOpen]);

    // Disable scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
        } else {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        }

        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = '0px';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const isFormValid = serial.trim().length > 0 && pin.trim().length > 0;

    const handleDownload = async () => {
        if (!isFormValid || isDownloading) return;

        setIsDownloading(true);
        setMessage(null);

        try {
            const result = await downloadWatchCard(serial.trim(), pin.trim());
            
            if (result.success && result.blob && result.filename) {
                // Create download link
                const url = window.URL.createObjectURL(result.blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = result.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                
                setMessage({ type: 'success', text: t.successMessage });
                
                // Close modal after success
                setTimeout(() => {
                    onClose();
                }, 1500);
            } else {
                // Show more specific error message if available
                const errorResponse = await result.response?.text();
                let errorMsg = t.errorMessage;
                
                if (errorResponse) {
                    try {
                        const errorData = JSON.parse(errorResponse);
                        if (errorData.error) {
                            errorMsg = errorData.error;
                        }
                    } catch (e) {
                        // Use default error message
                    }
                }
                
                setMessage({ type: 'error', text: errorMsg });
            }
        } catch (error) {
            setMessage({ type: 'error', text: t.errorMessage });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid && !isDownloading) {
            handleDownload();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center overflow-y-auto"
            onClick={handleOverlayClick}
        >
            <div className="watch-card-modal modal-content bg-white rounded-lg p-8 max-w-md w-full m-4 relative animate-modalEntry">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors"
                    disabled={isDownloading}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <h2 className="text-2xl font-light mb-4">{t.title}</h2>
                <p className="text-gray-600 mb-6">{t.description}</p>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="serial" className="block text-sm font-medium text-gray-700 mb-2">
                            {t.serialLabel}
                        </label>
                        <input
                            id="serial"
                            type="text"
                            value={serial}
                            onChange={(e) => setSerial(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={t.serialPlaceholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            disabled={isDownloading}
                        />
                    </div>

                    <div>
                        <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                            {t.pinLabel}
                        </label>
                        <input
                            id="pin"
                            type="text"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={t.pinPlaceholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                            disabled={isDownloading}
                        />
                    </div>

                    {message && (
                        <div className={`status-badge ${message.type === 'success' ? 'success' : 'error'}`}>
                            {message.text}
                        </div>
                    )}

                    <button
                        onClick={handleDownload}
                        disabled={!isFormValid || isDownloading}
                        className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
                            isFormValid && !isDownloading
                                ? 'bg-black text-white hover:bg-gray-800'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isDownloading ? t.downloadingButton : t.downloadButton}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default WatchCardModal;